import { useAuth0 } from "@auth0/auth0-react";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import { AppShell } from "../components/shell/AppShell";
import { ImportSuccessBannerProvider } from "../components/shell/ImportSuccessBanner";
import { NavPrefetcher } from "../components/shell/NavPrefetcher";
import { FirstRunWizard } from "../components/onboarding/FirstRunWizard";
import { TourProvider } from "../components/onboarding/TourProvider";
import { AddTradeDrawerProvider } from "../components/trades/AddTradeDrawer";
import { isBetaUnlocked } from "../lib/betaAccess";
import { AcceptanceGate } from "./AcceptanceGate";

export function ProtectedRoute() {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // L0-U5: route visitors who have not cleared the beta gate to the
      // landing page (the public front door) instead of straight to Auth0,
      // so the private-beta gate cannot be skipped by a deep link. Once the
      // gate is cleared, hand off to Auth0 as before.
      if (!isBetaUnlocked()) {
        void navigate({ to: "/" });
        return;
      }
      void loginWithRedirect({
        appState: {
          returnTo: window.location.pathname + window.location.search,
        },
      });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, navigate]);

  if (isLoading) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="min-h-screen flex items-center justify-center bg-surface text-on-surface-variant"
      >
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="min-h-screen flex items-center justify-center bg-surface text-on-surface-variant"
      >
        Redirecting to sign in...
      </div>
    );
  }

  return (
    <AcceptanceGate>
      <ImportSuccessBannerProvider>
        <AddTradeDrawerProvider
          onTradeCreated={(tradeId) => {
            void navigate({ to: "/trades/$tradeId", params: { tradeId } });
          }}
        >
          <TourProvider>
            <AppShell>
              <Outlet />
            </AppShell>
          </TourProvider>
          <FirstRunWizard />
          <NavPrefetcher />
        </AddTradeDrawerProvider>
      </ImportSuccessBannerProvider>
    </AcceptanceGate>
  );
}
